import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useOfflineManager - Manages offline/online state and message queuing
 * 
 * Features:
 * - Monitors network status (navigator.onLine + backend ping)
 * - Queues messages when offline
 * - Auto-sends queued messages when connection restored
 * - Persists queue in localStorage
 * - Provides reconnection state
 */
export const useOfflineManager = (apiBase) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState([]);
  const [lastPingTime, setLastPingTime] = useState(Date.now());
  const retryTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Load queued messages from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('ov_message_queue');
      if (stored) {
        const parsed = JSON.parse(stored);
        setQueuedMessages(parsed);
      }
    } catch (e) {
      // Failed to load queue from localStorage
    }
  }, []);

  // Save queued messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('ov_message_queue', JSON.stringify(queuedMessages));
    } catch (e) {
      // Failed to save queue to localStorage
    }
  }, [queuedMessages]);

  // Ping backend to verify actual connectivity
  const pingBackend = useCallback(async () => {
    if (!apiBase) return true;
    
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      
      // Backend exposes /healthz (not /health). Using /health caused false offline banners (405).
      const response = await fetch(`${apiBase}/healthz`, {
        method: 'GET',
        signal: controller.signal,
      });
      
      clearTimeout(timeout);
      setLastPingTime(Date.now());
      return response.ok;
    } catch (error) {
      // Don't log AbortError (signal aborted) as failures - these are timeouts/cleanups, not connectivity issues
      // Ping failed — AbortError means timeout/cleanup, not a connectivity issue
      return false;
    }
  }, [apiBase]);

  // Update online status based on network + backend
  const updateOnlineStatus = useCallback(async () => {
    const navigatorOnline = navigator.onLine;
    
    if (!navigatorOnline) {
      setIsOnline(false);
      setIsReconnecting(false);
      return;
    }

    // Navigator says online, but verify with backend
    setIsReconnecting(true);
    const backendOnline = await pingBackend();
    setIsOnline(backendOnline);
    setIsReconnecting(false);
  }, [pingBackend]);

  // Handle browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      updateOnlineStatus();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsReconnecting(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateOnlineStatus]);

  // Periodic ping to detect connection loss
  useEffect(() => {
    if (!apiBase) return;

    pingIntervalRef.current = setInterval(() => {
      if (navigator.onLine && !document.hidden) {
        pingBackend().then(backendOnline => {
          if (!backendOnline && isOnline) {
            setIsOnline(false);
          } else if (backendOnline && !isOnline) {
            setIsOnline(true);
          }
        }).catch(() => {
          // Ignore ping failures - don't mark offline on ping errors
        });
      }
    }, 30000); // Ping every 30 seconds

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [apiBase, isOnline, pingBackend]);

  // Queue a message when offline
  const queueMessage = useCallback((message) => {
    const queuedMsg = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      message,
      timestamp: Date.now(),
    };

    setQueuedMessages(prev => [...prev, queuedMsg]);

    return queuedMsg.id;
  }, []);

  // Send a specific queued message
  const sendQueuedMessage = useCallback(async (queuedMsg, sendFunction) => {
    try {
      await sendFunction(queuedMsg.message);
      
      // Remove from queue on success
      setQueuedMessages(prev => prev.filter(m => m.id !== queuedMsg.id));

      return true;
    } catch (error) {
      // Failed to send queued message
      return false;
    }
  }, []);

  // Process entire queue when coming online
  const processQueue = useCallback(async (sendFunction) => {
    if (!isOnline || queuedMessages.length === 0) return;

    for (const queuedMsg of [...queuedMessages]) {
      const sent = await sendQueuedMessage(queuedMsg, sendFunction);
      if (!sent) {
        break;
      }
    }
  }, [isOnline, queuedMessages, sendQueuedMessage]);

  // Auto-process queue when coming online
  useEffect(() => {
    if (isOnline && queuedMessages.length > 0) {
      // Small delay to let the connection stabilize
      retryTimeoutRef.current = setTimeout(() => {
        // Note: processQueue needs to be called manually with sendFunction
        // This effect just logs the readiness
      }, 1000);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isOnline, queuedMessages.length]);

  // Clear all queued messages
  const clearQueue = useCallback(() => {
    setQueuedMessages([]);
    try {
      localStorage.removeItem('ov_message_queue');
    } catch (e) {
      // Failed to clear queue from localStorage
    }
  }, []);

  return {
    isOnline,
    isReconnecting,
    queuedMessages,
    queuedCount: queuedMessages.length,
    lastPingTime,
    queueMessage,
    sendQueuedMessage,
    processQueue,
    clearQueue,
    updateOnlineStatus,
  };
};

export default useOfflineManager;
